import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MediaComponent } from '@shared/components/media/media.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { CarouselModule } from 'primeng/carousel';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IframeModule } from '@shared/components/iframe/iframe.module';
import { LocalLoaderComponent } from '@shared/components/loader/local-loader/local-loader.component';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { ScreenViewModule } from '@core/directives/screen-view/_screen-view.module';
import { DEVICE_SIZE } from '@core/models/deviceSize.model';
import { SafeURLModule } from '@core/pipes/safe-url/safe-url.module';
import { DeviceService } from '@shared/tools/device.service';
import { WindowRef } from '@shared/tools/window';
import { Product, ProductImageContainer, Video } from '@b2b/features/product/core/utilities/product.model';
import { TAB_GALLERY } from '@b2b/features/product/components/product-medias-modal/product-medias-modal.model';
const defaultThumbPath = '/src/../assets/medias/images/thumb-video.png';

@Component({
  selector: 'product-medias-modal',
  imports: [
    CommonModule,
    ScreenViewModule,
    TranslateModule,
    LocalLoaderComponent,
    NgxImageZoomModule,
    CarouselModule,
    IframeModule,
    SafeURLModule,
    MediaComponent,
  ],
  templateUrl: './product-medias-modal.component.html',
  styleUrl: './product-medias-modal.component.scss',
})
export class ProductMediasModalComponent implements OnInit, AfterViewInit, OnDestroy {
  TAB_GALLERY = TAB_GALLERY;
  DEVICE_SIZE = DEVICE_SIZE;
  subscription = new Subscription();

  product$ = new BehaviorSubject<Product | null>(null);
  currentTab$ = new BehaviorSubject<TAB_GALLERY>(TAB_GALLERY.PHOTO);

  images$ = new BehaviorSubject<ProductImageContainer[]>([]);
  currentImage$ = new BehaviorSubject<ProductImageContainer | undefined>(undefined);
  videos$ = new BehaviorSubject<Video[]>([]);
  currentVideo$ = new BehaviorSubject<Video | undefined>(undefined);

  displayIsLoad = true;

  constructor(
    private winRef: WindowRef,
    private renderer: Renderer2,
    private modalListService: ModalService<{
      product: Product | undefined;
      currentImage: ProductImageContainer | undefined;
      tab: TAB_GALLERY;
    }>,
    private deviceService: DeviceService,
  ) {}

  ngOnInit(): void {
    this.populate();

    if (this.currentTab$.getValue() === TAB_GALLERY.PHOTO) {
      this.configImage();
    }
    if (this.currentTab$.getValue() === TAB_GALLERY.VIDEO) {
      this.configVideo();
    }
  }

  ngAfterViewInit() {
    this.listenerIframe();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  zoomImageListner(event: any) {
    this.displayIsLoad = !event;
  }

  setTab(tab: TAB_GALLERY) {
    this.displayIsLoad = true;
    this.currentTab$.next(tab);

    if (tab === TAB_GALLERY.PHOTO) {
      this.configImage();
    }
    if (tab === TAB_GALLERY.VIDEO) {
      this.configVideo();
    }
  }

  configImage() {
    if (
      this.deviceService.getCurrentSize() === DEVICE_SIZE.MOBILE ||
      this.deviceService.getCurrentSize() === DEVICE_SIZE.TABLET
    ) {
      this.displayIsLoad = false;
    }
  }

  configVideo() {
    setTimeout(() => {
      this.listenerIframe();
    }, 500);
  }

  setCurrentImage(media: ProductImageContainer) {
    const isOtherMedia = this.currentImage$.getValue()?.['thumbnail']?.src !== media?.['thumbnail']?.src;

    if (isOtherMedia) {
      this.displayIsLoad = true;
      this.configImage();
      this.currentImage$.next(media);
    }
  }

  setCurrentVideo(video: Video) {
    const isOtherVideo = video.src !== this.currentVideo$.getValue()?.src;

    if (isOtherVideo) {
      this.displayIsLoad = true;
      this.currentVideo$.next(video);
    }
  }

  getThumbVideo(video: Video): string {
    if (video?.src?.includes('.youtube')) {
      const videoID = video.src.split('embed/')[1].split('?')[0];
      return `https://img.youtube.com/vi/${videoID}/mqdefault.jpg`;
    }

    return defaultThumbPath;
  }

  listenerIframe() {
    const videoIframe = this.winRef.document.querySelector('#videoIframe') as HTMLIFrameElement;

    if (videoIframe) {
      this.renderer.listen(videoIframe, 'load', () => {
        this.displayIsLoad = false;
      });
    }
  }

  populate() {
    this.subscription.add(
      this.modalListService.getDataByModalID('product-medias-modal').subscribe(content => {
        if (content.product) {
          const images = (content?.product && content?.product?.medias?.images) || [];
          const videos = content?.product?.medias?.videos;

          this.currentImage$.next(content.currentImage);
          this.images$.next(images);

          this.currentVideo$.next(videos?.[0] || undefined);
          this.videos$.next(videos || []);

          this.currentTab$.next(content.tab);
          this.product$.next(content.product);
        }
      }),
    );
  }
}
